import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Chrome, Apple, Phone, Mail, Lock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export default function Login() {
  const navigate = useNavigate();
  const { user, ready, login, register, loginWithGoogle } = useAuth();
  const [demoEnabled, setDemoEnabled] = useState(false);
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/demo-enabled`).then((r) => r.json()).then((d) => setDemoEnabled(!!d.enabled)).catch(() => {}); }, []);
  const [mode, setMode] = useState("login"); // login | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (ready && user) navigate("/home", { replace: true }); }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email.trim(), password);
      else {
        if (!name.trim()) { setError("Please enter a display name."); setBusy(false); return; }
        await register(email.trim(), password, name.trim());
      }
      navigate("/home", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const proto = (label) => toast(`${label} is prototype-only`, { description: "Use Email or Continue with Google for now." });

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-am-bg px-5 py-10">
      <div className="pointer-events-none absolute left-1/2 top-[-10%] h-[460px] w-[460px] -translate-x-1/2 aura-radial opacity-[0.16] blur-3xl" />

      <div className="relative w-full max-w-[400px]">
        <button onClick={() => navigate("/")} className="mx-auto mb-6 flex flex-col items-center gap-3" data-testid="login-logo">
          <Logo size={64} />
          <span className="font-mark text-xl font-extrabold tracking-tight text-white">AURAMAX</span>
        </button>

        <div className="rounded-3xl border border-am-border bg-am-surface/60 p-6 backdrop-blur-xl sm:p-7">
          <h1 className="font-display text-2xl font-extrabold text-white">
            {mode === "login" ? "Welcome back" : "Join AuraMax"}
          </h1>
          <p className="mt-1 text-sm text-am-text2">
            {mode === "login" ? "Log in to max your Aura." : "Create your account and start auramaxxing."}
          </p>

          {/* Social */}
          <div className="mt-6 space-y-2.5">
            <button onClick={loginWithGoogle} data-testid="login-google" className="flex w-full items-center justify-center gap-3 rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.01] active:scale-95">
              <Chrome className="h-5 w-5" /> Continue with Google
            </button>
            <div className="flex gap-2.5">
              <button onClick={() => proto("Apple sign-in")} data-testid="login-apple" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-am-border bg-am-surface2 py-3 text-sm font-medium text-am-text2 hover:text-white">
                <Apple className="h-4 w-4" /> Apple
              </button>
              <button onClick={() => proto("Phone sign-in")} data-testid="login-phone" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-am-border bg-am-surface2 py-3 text-sm font-medium text-am-text2 hover:text-white">
                <Phone className="h-4 w-4" /> Phone
              </button>
            </div>
          </div>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-am-border" />
            <span className="text-xs text-am-text3">or with email</span>
            <span className="h-px flex-1 bg-am-border" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name" placeholder="Display name" className="w-full rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong" />
            )}
            <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
              <Mail className="h-4 w-4 text-am-text3" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="auth-email" placeholder="Email" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
            </div>
            <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
              <Lock className="h-4 w-4 text-am-text3" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={6} data-testid="auth-password" placeholder="Password" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
            </div>

            {error && <p data-testid="auth-error" className="rounded-lg bg-aura-magenta/10 px-3 py-2 text-xs text-aura-magenta">{error}</p>}

            {mode === "login" && (
              <div className="flex justify-end">
                <button type="button" onClick={() => navigate("/forgot")} data-testid="forgot-link" className="text-xs font-semibold text-am-text2 hover:text-white">Forgot password?</button>
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="auth-submit" className="flex w-full items-center justify-center gap-2 rounded-full p-[1.5px] aura-gradient disabled:opacity-60">
              <span className="flex w-full items-center justify-center gap-2 rounded-full bg-am-bg py-3 text-sm font-bold text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Log in" : "Create account")}
                {!busy && <ArrowRight className="h-4 w-4" />}
              </span>
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-am-text2">
            {mode === "login" ? "New to AuraMax?" : "Already have an account?"}{" "}
            <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }} data-testid="auth-toggle" className="font-semibold aura-text">
              {mode === "login" ? "Create account" : "Log in"}
            </button>
          </p>
        </div>

        {demoEnabled && (
          <p className="mt-4 text-center text-xs text-am-text3" data-testid="login-demo-hint">
            Demo account: <span className="text-am-text2">demo@auramax.app</span> / <span className="text-am-text2">auramax123</span>
          </p>
        )}
      </div>
    </div>
  );
}
